import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '~/store';

import useAnalytics from '../useAnalytics/useAnalytics';

interface PageViewTrackingOptions {
  page_name: string;
  additional_properties?: Record<string, unknown>;
}

const usePageViewTracking = () => {
  const { track } = useAnalytics();

  // Get ecosystem and organization data from Redux store
  const selectedEcosystem = useSelector(
    (state: RootState) => state.ecosystem.selectedEcosystem,
  );
  const selectedUserProfile = useSelector(
    (state: RootState) => state.auth.selectedUserProfile,
  );

  const trackPageView = useCallback(
    ({ page_name, additional_properties = {} }: PageViewTrackingOptions) => {
      const currentUrl = window.location.href;
      const referrer = document.referrer || '';
      const path = window.location.pathname;
      const search = window.location.search;

      // Prepare ecosystem information
      const ecosystem_id =
        selectedEcosystem?._id || selectedUserProfile?.ecosystem;
      const ecosystem_name =
        selectedEcosystem?.name || selectedUserProfile?.ecosystemSummary?.name;

      // Prepare organization information
      const org_id = selectedUserProfile?.organization;
      // Note: userOrganization is populated by useVolunteerUser hook and contains the full organization object
      // We need to cast it properly since the interface shows it as string but it's actually an object
      const org_name = (selectedUserProfile?.userOrganization as any)?.name;

      // Prepare user information
      const user_id = selectedUserProfile?._id;

      const pageViewProperties = {
        page_name,
        timestamp: Date.now(),
        url: currentUrl,
        referrer,
        path,
        search,
        user_id,
        ecosystem_id,
        ecosystem_name,
        org_id,
        org_name,
        ...additional_properties,
      };

      // Remove undefined values to keep the event clean
      const cleanProperties = Object.fromEntries(
        Object.entries(pageViewProperties).filter(
          ([, value]) => value !== undefined && value !== null && value !== '',
        ),
      );

      track('page_view', cleanProperties as any);
    },
    [track, selectedEcosystem, selectedUserProfile],
  );

  return {
    trackPageView,
  };
};

export default usePageViewTracking;
